import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import StoreX, { store } from '../../redux/oldStore';
import { ServerResponse } from '../../utils/Server';
import Button from '../_Core/Button';
import Modal from '../Modal/Modal';
import redux, { IState, ReduxMap } from '../../redux/redux';

interface ICoordinatorPerson {
  PersonId: number;
  FirstName: string;
  LastName: string;
  Email: string;
  SecurityRole: string;
}

interface IAffiliateRow {
  Id: number;
  LocationCode: string;
  ClientId: number;
  Coordinators: ICoordinatorPerson[];
}

interface IStaffPerson {
  Id: number;
  FirstName: string;
  LastName: string;
  Email: string;
  SecurityRole: string;
}

interface IAffiliateCoordinatorData {
  People: IStaffPerson[];
  Affiliates: IAffiliateRow[];
}

const AffiliateCoordinatorPage = (props: IState) => {
  const [data, setData] = useState<IAffiliateCoordinatorData | null>(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [affiliateId, setAffiliateId] = useState(0);
  const [personId, setPersonId] = useState(0);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    store.server
      .postApi<ServerResponse<IAffiliateCoordinatorData>>('../AffiliateCoordinator/Data', {})
      .then((x) => {
        if (x.Success) {
          setData(x.Value);
        } else {
          if (x.LoginRequired) redux.showLogin(true);
          toast.error(x.Message || 'Unable to load coordinators.', { autoClose: false });
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (props.User) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.User]);

  const openAdd = (affId: number) => {
    setAffiliateId(affId);
    setPersonId(0);
    setShowModal(true);
  };

  const save = () => {
    if (!affiliateId) {
      toast.warning('You must select an Affiliate.');
      return;
    }
    if (!personId) {
      toast.warning('You must select a Coordinator.');
      return;
    }
    setSaving(true);
    store.server
      .postApi<ServerResponse<IAffiliateCoordinatorData>>('../AffiliateCoordinator/SaveCoordinatorJson', {
        affiliateId,
        personId,
      })
      .then((x) => {
        if (x.Success) {
          setData(x.Value);
          setShowModal(false);
          toast.success('Coordinator saved.');
        } else {
          toast.error(x.Message, { autoClose: false });
        }
      })
      .finally(() => setSaving(false));
  };

  const remove = (affId: number, pId: number) => {
    if (!window.confirm('Are you sure you want to remove this coordinator?')) return;
    store.server
      .postApi<ServerResponse<IAffiliateCoordinatorData>>('../AffiliateCoordinator/RemoveCoordinatorJson', {
        affiliateId: affId,
        personId: pId,
      })
      .then((x) => {
        if (x.Success) {
          setData(x.Value);
          toast.success('Coordinator removed.');
        } else {
          toast.error(x.Message, { autoClose: false });
        }
      });
  };

  if (loading || !data) {
    return (
      <div className="main-body-page grow" data-testid="affiliate-coordinator-page">
        <h2>Affiliate Coordinator</h2>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="main-body-page grow" data-testid="affiliate-coordinator-page">
      <div className="flex-between">
        <h2>
          <i className="fa fa-file-text-o" /> Affiliate Coordinator
        </h2>
        <Button type="refresh" text="Refresh" onClick={load} />
      </div>
      <table className="table table-striped table-hover" data-testid="affiliate-coordinator-table">
        <thead>
          <tr>
            <th>Affiliate</th>
            <th>
              <a href={StoreX.BuildUrlWithFair('../app/purchase/admin-invoice-summary')} target="_blank" rel="noreferrer">
                <i className="fa fa-file-text-o" /> Invoice Summary
              </a>
              <br />
              <a
                href={StoreX.BuildUrlWithFair('../app/purchase/admin-invoice-summary?onlyCardTransactions=true')}
                target="_blank"
                rel="noreferrer"
              >
                <i className="fa fa-file-text-o" /> Invoice Summary (Card Only)
              </a>
            </th>
            <th>Coordinators</th>
          </tr>
        </thead>
        <tbody>
          {data.Affiliates.map((a) => (
            <tr key={a.Id} data-testid={`affiliate-row-${a.Id}`}>
              <td>{a.LocationCode}</td>
              <td>
                <a
                  href={StoreX.BuildUrlWithFair(`../app/purchase/affiliate-invoice-summary/${a.ClientId}`)}
                  target="_blank"
                  rel="noreferrer"
                >
                  <i className="fa fa-file-text-o" /> Invoice Summary
                </a>
              </td>
              <td>
                <div>
                  <button type="button" className="btn btn-xs btn-success" data-testid={`add-coordinator-${a.Id}`} onClick={() => openAdd(a.Id)}>
                    <i className="fa fa-user-plus" /> Add Coordinator
                  </button>
                </div>
                <div>
                  {(a.Coordinators || []).map((c) => (
                    <div key={c.PersonId} data-testid={`coordinator-${a.Id}-${c.PersonId}`}>
                      <span className="text-button" style={{ cursor: 'pointer' }} onClick={() => remove(a.Id, c.PersonId)} title="Remove">
                        <i className="fa fa-trash" />
                      </span>
                      &nbsp;&nbsp;
                      {c.FirstName} {c.LastName} - {c.Email} ({c.SecurityRole})
                    </div>
                  ))}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {showModal && (
        <Modal title="Affiliate Coordinator" setModalOpen={setShowModal}>
          <div className="form-horizontal" data-testid="add-coordinator-modal">
            <div className="form-group">
              <label className="col-sm-4 control-label">Affiliate</label>
              <div className="col-sm-8">
                <select className="form-control" data-testid="coordinator-affiliate" value={affiliateId} onChange={(e) => setAffiliateId(+e.target.value)}>
                  <option value={0}>Select...</option>
                  {data.Affiliates.map((a) => (
                    <option key={a.Id} value={a.Id}>
                      {a.LocationCode}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="col-sm-4 control-label">Coordinator</label>
              <div className="col-sm-8">
                <select className="form-control" data-testid="coordinator-person" value={personId} onChange={(e) => setPersonId(+e.target.value)}>
                  <option value={0}>Select...</option>
                  {data.People.map((p) => (
                    <option key={p.Id} value={p.Id}>
                      {p.LastName}, {p.FirstName} ({p.SecurityRole})
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="text-right">
              <Button type="save" text={saving ? 'Saving...' : 'Save'} onClick={save} disabled={saving} />
              <Button type="cancel" text="Close" onClick={() => setShowModal(false)} />
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default ReduxMap(AffiliateCoordinatorPage);
